import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Modal,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { ReactComponent as Close } from 'assets/images/ic_close.svg';
import { ReactElement, useEffect, useState } from 'react';
import {
  AppFormikButtonTiles,
  AppFormikTextField,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
  ConfirmDialog,
} from 'shared/components/AppComponents';
import PageHeader from 'shared/components/PageHeader';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import { Formik, FormikHelpers } from 'formik';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';
import { EmployeeSchema } from 'validators/ManageEmployeesScheme';
import { ManageEmployeesService } from 'services/ManageEmployeesService';
import { Toast, Utils } from 'shared/helpers';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as Upload } from 'assets/images/ic_upload.svg';
import DialogBulkUpload from 'modules/ai-inventory/DialogBulkUpload';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';
import { PaginationActions } from 'state/pagination/Action';

export interface IEmployeeList {
  _id: string;
  organizationId: string;
  name: string;
  email: string;
  department: string;
  role: string;
  created_by: string;
  updatedDate: string | null;
  isActive: boolean;
  createdDate: string;
  __v: number;
}

const ManageEmployees = (): ReactElement => {
  const dispatch = useDispatch();
  const employeePagination = useSelector(
    (state: IAppState) => state.paginationState.employeeTable
  );
  // State Variables
  const [isLoading, setIsLoading] = useState(false);
  const [openDelete, setOpenDelete] = useState<any>(null);
  const [editingEmployee, setEditingEmployee] = useState<any>(null);
  const [manageEmployeesList, setManageEmployeesList] = useState<
    IEmployeeList[]
  >([]);
  const [openEdit, setOpenEdit] = useState<any>(null);
  const [openBulk, setOpenBulk] = useState<any>(false);

  const navigate = useNavigate();

  // Constants
  // API Calls
  const onLoadEmployeesList = (): void => {
    setIsLoading(true);

    const queryString = new URLSearchParams({
      offset: String(employeePagination.offset),
      limit: String(employeePagination.pageLimit),
      search: employeePagination.search,
    }).toString();

    ManageEmployeesService.getAll(queryString)
      .then((response) => {
        const { employee, pagination } = response.data.data;
        setManageEmployeesList(employee);
        dispatch(
          PaginationActions.setPagination({
            employeeTable: {
              offset: pagination.offset,
              pageLimit: pagination.limit,
              totalRecords: pagination.total,
              totalPages: pagination.totalPages,
            },
          })
        );
      })
      .catch((error: any) => {
        Toast.showInfoMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    onLoadEmployeesList();
  }, [
    employeePagination.offset,
    employeePagination.pageLimit,
    employeePagination.search,
  ]);
  // UI Action

  const handleCloseDelete = (): void => {
    setOpenDelete(false);
  };

  const handleDelete = (row: any): void => {
    ManageEmployeesService.deleteRecord(row._id)
      .then(() => {
        handleCloseDelete();
        onLoadEmployeesList();
      })
      .catch((error) => {
        Toast.showInfoMessage(error.message);
      });
  };

  const actionOnAddNewEmployee = (setFieldValue: any): void => {
    setFieldValue('status', [false, true]);
    setEditingEmployee(null);
  };

  const actionOnFormSubmit = async (
    formValues: any,
    formik: FormikHelpers<any>,
    setFieldValue?: any
  ): Promise<void> => {
    const { empRole, email, name, department } = formValues;
    const payload = {
      name,
      email,
      department,
      empRole,
    };

    const promise = editingEmployee?._id
      ? ManageEmployeesService.update(editingEmployee._id, payload)
      : ManageEmployeesService.create(payload);
    promise
      .then(() => {
        formik.resetForm();
        setEditingEmployee(null);
        onLoadEmployeesList();
        setOpenEdit(false);
        if (setFieldValue) setFieldValue('status', [true, false]);
      })
      .catch((error) => Utils.handleErrorResponse(error))
      .finally(() => {
        formik.setSubmitting(false);
      });
  };
  // Instance Methods
  const handlePageChange = (_: unknown, page: number): void => {
    dispatch(
      PaginationActions.setPagination({
        employeeTable: {
          offset: page + 1,
        },
      })
    );
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const pageLimit = Number(event.target.value);

    dispatch(
      PaginationActions.setPagination({
        employeeTable: {
          offset: 1,
          pageLimit,
        },
      })
    );
  };

  // UI Elements
  return (
    <Box className="mx-2 my-5">
      <Formik
        initialValues={{ status: [true, false] }}
        enableReinitialize
        onSubmit={() => console.log('onSubmit')}
      >
        {({ values, setFieldValue }) => (
          <>
            <PageHeader
              leftBackButton={
                <IconButton
                  onClick={() => {
                    navigate('/settings?tabIndex=2');
                  }}
                >
                  <ArrowBackIosIcon />
                </IconButton>
              }
              title="Employee Management"
              description="Manage your organisation's employees"
              actions={
                <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                  <AppPrimaryButtonOutlined
                    title="Bulk Upload"
                    className="btn-small"
                    startIcon={
                      <SvgIcon
                        component={Upload}
                        inheritViewBox
                        fontSize="inherit"
                      />
                    }
                    onClick={() => setOpenBulk(true)}
                  />
                  <AppPrimaryButton
                    className="btn-small"
                    onClick={() => {
                      actionOnAddNewEmployee(setFieldValue);
                    }}
                    startIcon={
                      <SvgIcon
                        component={Plus}
                        inheritViewBox
                        className="view-icon"
                        fontSize="inherit"
                      />
                    }
                    title="Add Employee"
                  />
                </Box>
              }
            />
            <Stack className="mx-8" direction="column" spacing={2}>
              <AppFormikButtonTiles
                variant="outlined"
                name="status"
                size="small"
                label=""
                disabled={false}
                multichoice={false}
                className="app-group-button"
                buttons={[
                  {
                    value: 'Employee',
                  },
                  {
                    value: 'New Employee',
                    onClick: () => {
                      setEditingEmployee(null);
                    },
                  },
                ]}
                outerLabel
              />
              {values.status[0] && (
                <>
                  {isLoading && (
                    <Box className="empty-content">
                      <CircularProgress />
                    </Box>
                  )}
                  {manageEmployeesList.length === 0 && (
                    <Box className="empty-content">
                      <EmptyPlaceholder title="No record found" />
                    </Box>
                  )}
                  {!isLoading && manageEmployeesList.length > 0 && (
                    <TableContainer>
                      <Table className="app-table">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Department</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {manageEmployeesList.map((row) => (
                            <TableRow key={row._id}>
                              <TableCell className="word-break">
                                {row.name}
                              </TableCell>
                              <TableCell>{row.email}</TableCell>
                              <TableCell>{row.role}</TableCell>
                              <TableCell>{row.department}</TableCell>
                              <TableCell>
                                <Box
                                  style={{
                                    display: 'flex',
                                    columnGap: '10px',
                                  }}
                                >
                                  <AppPrimaryButtonOutlined
                                    className="btn-small"
                                    onClick={() => {
                                      setEditingEmployee(row);
                                      setOpenEdit(row);
                                    }}
                                    title="Edit"
                                    startIcon={<EditIcon />}
                                  />
                                  <AppPrimaryButtonOutlined
                                    className="btn-small"
                                    onClick={() => {
                                      setOpenDelete(row);
                                    }}
                                    title="Delete"
                                    startIcon={<DeleteIcon />}
                                  />
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}

              {values.status[1] && (
                <Box className="app-plain-card">
                  <Box className="content">
                    <Formik
                      enableReinitialize
                      initialValues={{
                        email: editingEmployee?.email ?? '',
                        name: editingEmployee?.name ?? '',
                        empRole: editingEmployee?.role ?? '',
                        department: editingEmployee?.department ?? '',
                      }}
                      validationSchema={EmployeeSchema}
                      validateOnMount
                      onSubmit={(formValues, formik) => {
                        actionOnFormSubmit(formValues, formik, setFieldValue);
                      }}
                    >
                      {({ handleSubmit, isSubmitting }) => (
                        <form autoComplete="off">
                          <Box>
                            <Typography variant="h4">Name*</Typography>
                            <AppFormikTextField
                              name="name"
                              placeholder="Enter your name"
                            />
                          </Box>
                          <Box>
                            <Typography variant="h4">Email Address*</Typography>
                            <AppFormikTextField
                              name="email"
                              placeholder="team@example.com"
                            />
                          </Box>
                          <Box>
                            <Typography variant="h4">Department*</Typography>
                            <AppFormikTextField
                              name="department"
                              placeholder=""
                            />
                          </Box>
                          <Box>
                            <Typography variant="h4">Role*</Typography>
                            <AppFormikTextField name="empRole" placeholder="" />
                          </Box>

                          <Box pt={6}>
                            <Grid container spacing={1}>
                              <Grid
                                item
                                xs={12}
                                sm={12}
                                md={12}
                                className="end-content"
                              >
                                <AppPrimaryButton
                                  className="p-3"
                                  testid="button-forgot"
                                  fullWidth
                                  title="Save"
                                  isLoading={isSubmitting}
                                  loadingSize={22}
                                  onClick={handleSubmit}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </form>
                      )}
                    </Formik>
                  </Box>
                </Box>
              )}
            </Stack>
          </>
        )}
      </Formik>

      {openBulk && (
        <DialogBulkUpload
          openBulk={openBulk}
          onClose={(reload: boolean) => {
            if (reload) {
              onLoadEmployeesList();
            }
            setOpenBulk(false);
          }}
          title="Bulk upload employees"
          variant="employees"
        />
      )}

      <ConfirmDialog
        title="Delete Employee"
        subTitle="Are you sure to delete the employee?"
        showConfirm={openDelete}
        cancelTitle="Close"
        successTitle="Delete"
        onSuccess={() => {
          handleCloseDelete();
          handleDelete(openDelete);
        }}
        onCancel={handleCloseDelete}
        onCloseDialog={handleCloseDelete}
      />

      <Modal
        open={openEdit}
        disableEscapeKeyDown
        onClose={handleCloseDelete}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
      >
        <Formik
          initialValues={{
            name: openEdit?.name,
            email: openEdit?.email,
            department: openEdit?.department,
            empRole: openEdit?.role,
          }}
          enableReinitialize
          validateOnMount
          validationSchema={null}
          onSubmit={(formValues, formik) => {
            actionOnFormSubmit(formValues, formik);
          }}
        >
          {({ handleSubmit }) => (
            <Box className="container-token-creation-modal">
              <Box
                display="flex"
                justifyContent="space-between"
                className="closemodal"
              >
                <Typography
                  sx={{
                    fontSize: '24px',
                    fontWeight: 700,
                    padding: '1rem 0',
                  }}
                >
                  Edit employee
                </Typography>
                <IconButton onClick={() => setOpenEdit(false)}>
                  <SvgIcon
                    component={Close}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                  />
                </IconButton>
              </Box>

              <form
                autoComplete="off"
                style={{
                  padding: '0 2rem 2rem 2rem',
                }}
              >
                <Box>
                  <Typography variant="h4">Name*</Typography>
                  <AppFormikTextField
                    name="name"
                    placeholder="Enter your name"
                  />
                </Box>
                <Box>
                  <Typography variant="h4">Email Address*</Typography>
                  <AppFormikTextField
                    name="email"
                    placeholder="team@example.com"
                  />
                </Box>
                <Box>
                  <Typography variant="h4">Department*</Typography>
                  <AppFormikTextField name="department" placeholder="" />
                </Box>
                <Box>
                  <Typography variant="h4">Role*</Typography>
                  <AppFormikTextField name="empRole" placeholder="" />
                </Box>

                <Box pt={6}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={12} className="end-content">
                      <AppPrimaryButton
                        className="p-3"
                        testid="button-forgot"
                        fullWidth
                        title="Update"
                        loadingSize={22}
                        onClick={handleSubmit}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </form>
            </Box>
          )}
        </Formik>
      </Modal>

      {!isLoading && manageEmployeesList.length > 0 && (
        <TablePagination
          component="div"
          count={employeePagination.totalRecords}
          page={employeePagination.offset - 1}
          rowsPerPage={employeePagination.pageLimit}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50]}
          disabled={isLoading}
        />
      )}
    </Box>
  );
};

export default ManageEmployees;
